import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';

import type {
  GqlBackgroundTaskOutput,
  GqlBackgroundTaskTypeEnum,
} from '@app-types/index';
import { GqlEntityKindEnum } from '@app-types/index';

import type {
  BackgroundTask as DisplayTask,
  TaskStatus,
} from '@components/marble/BackgroundTasksTray';

const defaultLabelByType = (t: GqlBackgroundTaskTypeEnum): string => {
  switch (t) {
    case 'AI_SEARCH':
      return 'AI Search';
    case 'DEEP_LOOKUP':
      return 'Deep Lookup';
    case 'AI_CHAT':
      return 'Chat session';
    default:
      return t;
  }
};

const ctaLabelByKind = (
  kind: GqlEntityKindEnum | null | undefined,
): string | undefined => {
  if (!kind) return undefined;
  switch (kind) {
    case GqlEntityKindEnum.AiSearchHistory:
      return 'View results';
    case GqlEntityKindEnum.AiChatSession:
      return 'View in chat';
    default:
      // Phase A: Auth / Draft / Unknown are non-clickable.
      return undefined;
  }
};

const statusToDisplay = (s: string): TaskStatus => {
  switch (s) {
    case 'RUNNING':
      return 'running';
    case 'FAILED':
      return 'failed';
    case 'PARTIAL':
      return 'done';
    case 'DONE':
      return 'done';
    default:
      // Forward-compat: unknown status renders as running so the card is
      // never invisible due to an enum mismatch after a server bump.
      return 'running';
  }
};

const buildOnOpen = (
  task: GqlBackgroundTaskOutput,
  navigate: NavigateFunction,
): (() => void) | undefined => {
  if (task.entityRef) {
    const ref = task.entityRef;
    switch (ref.kind) {
      case GqlEntityKindEnum.AiSearchHistory:
        return () => navigate(`/search/ai/${ref.id}`);
      case GqlEntityKindEnum.AiChatSession:
        return () => navigate(`/ai-chat/${ref.id}`);
      case GqlEntityKindEnum.Auth:
        // Phase A: non-clickable. Programmatic AuthImpactDrawer open
        // requires a `?openAuth=` query-param handler on Saved Creators
        // (Phase B).
        return undefined;
      case GqlEntityKindEnum.Draft:
      case GqlEntityKindEnum.Unknown:
        return undefined;
      default:
        return undefined;
    }
  }
  // Legacy fallback for rows pre-dating `entityRef` (AI Chat during
  // burn-in). External URL — open in a new tab to match prior behaviour.
  // Drop this branch in the follow-up cleanup once burn-in completes.
  if (task.resultUrl) {
    const url = task.resultUrl;
    return () => window.open(url, '_blank');
  }
  return undefined;
};

const toDisplay = (
  task: GqlBackgroundTaskOutput,
  navigate: NavigateFunction,
): DisplayTask => {
  const onOpen = buildOnOpen(task, navigate);
  return {
    id: task.id,
    title: task.title ?? defaultLabelByType(task.taskType),
    description: task.error ?? undefined,
    status: statusToDisplay(task.status),
    progress: task.progressPct ?? undefined,
    ctaLabel: onOpen ? ctaLabelByKind(task.entityRef?.kind) : undefined,
    onOpen,
  };
};

/**
 * Maps `GqlBackgroundTaskOutput[]` from the tray hook into the marble-layer
 * `BackgroundTask[]` UI shape, wiring `onOpen` handlers per `entityRef.kind`
 * with a legacy `resultUrl` fallback.
 */
export const useTrayDisplayTasks = (
  tasks: GqlBackgroundTaskOutput[],
): DisplayTask[] => {
  const navigate = useNavigate();
  return useMemo(
    () => tasks.map((task) => toDisplay(task, navigate)),
    [tasks, navigate],
  );
};

// Exported for unit tests.
export const __internal = {
  defaultLabelByType,
  ctaLabelByKind,
  statusToDisplay,
  buildOnOpen,
  toDisplay,
};
